import { useRef, useState } from "react";
import { Volume2, Pause } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

/**
 * "How to use this module" — the admin-recorded/uploaded voice instruction
 * for the student's current language (falls back to English if that locale
 * has none authored yet) plus its accompanying text. A single compact,
 * always-visible box (not a click-to-reveal modal) at the top of the module
 * screen, above Learn/Test — audio only plays when the student presses
 * Listen. Renders nothing if no module has content for either.
 */
export default function ModuleInstructionsHelp({ module: m }) {
  const { lang, t } = useI18n();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const instructions = m?.instructions || {};
  const own = instructions[lang];
  const entry = own && (own.text || own.audio_url) ? own : instructions.en;
  if (!entry || (!entry.text && !entry.audio_url)) return null;

  const toggle = () => {
    if (!audioRef.current || !entry.audio_url) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); return; }
    audioRef.current.src = `${process.env.REACT_APP_BACKEND_URL}${entry.audio_url}`;
    audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
  };

  return (
    <div data-testid="module-instructions-help"
      className="mt-4 rounded-xl bg-[#ECFDF5] border border-[#A7F3D0] px-3.5 py-2.5 flex items-center gap-3 flex-wrap">
      {entry.text && (
        <div className="flex-1 min-w-[200px]" data-testid="module-help-box">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#064E3B] font-semibold">{t("ms.how_to_use")}</div>
          <p className="text-xs text-[#1F2937] leading-snug mt-0.5 whitespace-pre-line">{entry.text}</p>
        </div>
      )}
      {entry.audio_url && (
        <button
          type="button"
          data-testid="module-help-audio-stripe"
          onClick={toggle}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
            playing ? "bg-[#047857] text-white" : "bg-[#064E3B] hover:bg-[#047857] text-white"
          }`}>
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          {playing ? "Playing…" : "Listen"}
        </button>
      )}
      <audio ref={audioRef} onEnded={() => setPlaying(false)} preload="none" />
    </div>
  );
}
